import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Modal } from "../../components/common/Modal";
import { Select } from "../../components/common/Select";
import { bulkSyncTime, type BulkSyncTimeResult, type BulkSyncTimePayload } from "../../api/devices";
import type { DstSection } from "../../api/config";
import { TIMEZONES, DST_PRESET_CHILE } from "../../api/timezones";
import { DstSectionEditor } from "../deviceConfig/TimeTab";

interface Props {
  open: boolean;
  deviceIds: number[];
  onClose: () => void;
}

const TZ_OPTS = TIMEZONES.map(t => ({
  value: String(t.value),
  label: t.hint ? `${t.label} — ${t.hint}` : t.label,
}));

function fmtLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
function toDtInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function blankSection(): DstSection {
  return { Year: 0, Month: 1, Week: 0, Day: 0, Hour: 0, Minute: 0 };
}

export function BulkSyncTimeModal({ open, deviceIds, onClose }: Props) {
  const [timeMode, setTimeMode] = useState<"now" | "custom" | "off">("now");
  const [customTime, setCustomTime] = useState<string>(toDtInputValue(new Date()));

  const [tzEnable, setTzEnable] = useState(true);
  const [tz, setTz] = useState<number>(24);

  const [dstEnable, setDstEnable] = useState(false);
  const [dst, setDst] = useState<{ Enable: boolean; Start: DstSection; End: DstSection }>({
    Enable: true, Start: blankSection(), End: blankSection(),
  });

  const [ntpSection, setNtpSection] = useState(false);
  const [ntpEnable, setNtpEnable] = useState(true);
  const [ntpAddress, setNtpAddress] = useState("pool.ntp.org");
  const [ntpPort, setNtpPort] = useState(123);
  const [ntpPeriod, setNtpPeriod] = useState(60);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkSyncTimeResult | null>(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      setTimeMode("now");
      setCustomTime(toDtInputValue(new Date()));
      setTzEnable(true);
      setTz(24);
      setDstEnable(false);
      setNtpSection(false);
    }
  }, [open]);

  const applyChilePreset = () => {
    setDst({
      ...DST_PRESET_CHILE,
      Start: { ...DST_PRESET_CHILE.Start, Year: new Date().getFullYear() },
      End:   { ...DST_PRESET_CHILE.End,   Year: new Date().getFullYear() + 1 },
    });
  };

  const submit = async () => {
    if (submitting) return;
    const payload: BulkSyncTimePayload = { device_ids: deviceIds };
    if (timeMode === "now") payload.time = fmtLocal(new Date());
    else if (timeMode === "custom" && customTime) payload.time = customTime.replace("T", " ") + ":00";
    if (tzEnable) payload.timezone = tz;
    if (dstEnable) payload.dst = dst;
    if (ntpSection) payload.ntp = { Enable: ntpEnable, Address: ntpAddress, Port: ntpPort, UpdatePeriod: ntpPeriod };

    if (!payload.time && payload.timezone === undefined && !payload.dst && !payload.ntp) {
      toast.error("Tenes que elegir al menos una cosa para aplicar (hora, zona, DST o NTP)");
      return;
    }

    setSubmitting(true);
    const t = toast.loading(`Sincronizando ${deviceIds.length} dispositivo${deviceIds.length === 1 ? "" : "s"}...`);
    try {
      const r = await bulkSyncTime(payload);
      setResult(r);
      if (r.errors.length === 0) {
        toast.success(`OK en ${r.success.length} dispositivo${r.success.length === 1 ? "" : "s"}`, { id: t });
      } else {
        toast.error(`${r.success.length} OK, ${r.errors.length} con error`, { id: t });
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Error en el envío bulk", { id: t });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Sincronizar hora en ${deviceIds.length} dispositivo${deviceIds.length === 1 ? "" : "s"}`} width="xl">
      <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto pr-1">

        <section className="border border-line rounded p-3 bg-panel/30 space-y-2">
          <h3 className="text-sm font-semibold">Hora</h3>
          <label className="flex items-center gap-2">
            <input type="radio" name="bulkTimeMode" checked={timeMode === "now"} onChange={() => setTimeMode("now")} />
            <span>Hora de mi computadora ahora (<span className="font-mono text-xs">{fmtLocal(new Date())}</span>)</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="bulkTimeMode" checked={timeMode === "custom"} onChange={() => setTimeMode("custom")} />
            <span>Hora personalizada</span>
            <input
              type="datetime-local"
              className="bg-line border border-line rounded px-2 py-1 text-xs font-mono"
              value={customTime}
              onChange={(e) => { setCustomTime(e.target.value); setTimeMode("custom"); }}
            />
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="bulkTimeMode" checked={timeMode === "off"} onChange={() => setTimeMode("off")} />
            <span>No cambiar la hora (solo aplicar zona/DST/NTP)</span>
          </label>
        </section>

        <section className="border border-line rounded p-3 bg-panel/30 space-y-2">
          <label className="flex items-center gap-2 font-semibold">
            <input type="checkbox" checked={tzEnable} onChange={(e) => setTzEnable(e.target.checked)} />
            <span>Aplicar zona horaria</span>
          </label>
          <div className={tzEnable ? "" : "opacity-40 pointer-events-none"}>
            <Select
              options={TZ_OPTS}
              value={String(tz)}
              onChange={(e) => setTz(Number(e.target.value))}
            />
          </div>
        </section>

        <section className="border border-line rounded p-3 bg-panel/30 space-y-2">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 font-semibold">
              <input type="checkbox" checked={dstEnable} onChange={(e) => setDstEnable(e.target.checked)} />
              <span>Aplicar configuración de horario de verano</span>
            </label>
            <Button size="sm" variant="ghost" onClick={applyChilePreset} type="button" disabled={!dstEnable}>
              Preset Chile
            </Button>
          </div>
          <div className={dstEnable ? "space-y-2" : "space-y-2 opacity-40 pointer-events-none"}>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={dst.Enable} onChange={(e) => setDst({ ...dst, Enable: e.target.checked })} />
              <span>Habilitar horario de verano en los dispositivos</span>
            </label>
            <div className={dst.Enable ? "space-y-2" : "space-y-2 opacity-40 pointer-events-none"}>
              <DstSectionEditor label="Inicio" section={dst.Start} onChange={(p) => setDst({ ...dst, Start: { ...dst.Start, ...p } })} />
              <DstSectionEditor label="Fin" section={dst.End} onChange={(p) => setDst({ ...dst, End: { ...dst.End, ...p } })} />
            </div>
          </div>
        </section>

        <section className="border border-line rounded p-3 bg-panel/30 space-y-2">
          <label className="flex items-center gap-2 font-semibold">
            <input type="checkbox" checked={ntpSection} onChange={(e) => setNtpSection(e.target.checked)} />
            <span>Aplicar configuración NTP</span>
          </label>
          <div className={ntpSection ? "space-y-2" : "space-y-2 opacity-40 pointer-events-none"}>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={ntpEnable} onChange={(e) => setNtpEnable(e.target.checked)} />
              <span>Habilitar sincronización NTP automática</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <Input label="Servidor" value={ntpAddress} onChange={(e) => setNtpAddress(e.target.value)} placeholder="pool.ntp.org" />
              <Input label="Puerto" type="number" min={1} max={65535} value={String(ntpPort)} onChange={(e) => setNtpPort(Number(e.target.value) || 123)} />
              <Input label="Frecuencia (min)" type="number" min={1} max={1440} value={String(ntpPeriod)} onChange={(e) => setNtpPeriod(Number(e.target.value) || 60)} />
            </div>
          </div>
        </section>

        {result && (
          <section className="border border-line rounded p-3 bg-panel space-y-2">
            <div className="grid grid-cols-2 gap-3 text-center mb-1">
              <div>
                <div className="text-2xl font-semibold text-green-500">{result.success.length}</div>
                <div className="text-xs text-muted">Exitosos</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-red-500">{result.errors.length}</div>
                <div className="text-xs text-muted">Con error</div>
              </div>
            </div>
            {result.errors.length > 0 && (
              <div className="text-xs">
                <div className="font-semibold mb-1">Errores:</div>
                <ul className="space-y-1 max-h-32 overflow-auto">
                  {result.errors.map((er, i) => (
                    <li key={i} className="text-red-400">
                      {er.name || `device ${er.device_id}`}: {er.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

      </div>

      <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-line">
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Cerrar</Button>
        <Button variant="primary" onClick={submit} loading={submitting} disabled={deviceIds.length === 0}>
          {result ? "Volver a aplicar" : "Aplicar"}
        </Button>
      </div>
    </Modal>
  );
}
